import React, { useEffect, useState, useCallback, useMemo } from 'react';
import apiService from '../services/api';
import {
  CalibrationContextTurn,
  CalibrationItemDto,
  CalibrationProgress,
  CalibrationListItem,
} from '../types';

type ScoreValue = 1 | 2 | 3;

type ScoreKey =
  | 'emotionalAuthenticityScore'
  | 'groundingScore'
  | 'topicConsistencyScore';

type StepId = 'step-rubric' | 'step-context' | 'step-score';

interface RubricLevel {
  title: string;
  detail: string;
}

interface DimensionConfig {
  key: ScoreKey;
  label: string;
  short: string;
  levels: Record<ScoreValue, RubricLevel>;
}

interface StepConfig {
  id: StepId;
  title: string;
  detail: string;
}

interface QueueCaseGroup {
  caseNumber: number;
  caseLabel: string;
  turns: CalibrationListItem[];
}

interface DimensionNotes {
  emotionalAuthenticity: string;
  grounding: string;
  topicConsistency: string;
}

const STEP_CONFIG: StepConfig[] = [
  {
    id: 'step-rubric',
    title: 'Step 1: Review Rubric',
    detail: 'First, read the 1/2/3 definitions so your scores stay consistent.',
  },
  {
    id: 'step-context',
    title: 'Step 2: Review Context',
    detail: 'Second, read context, clinician last line, and target parent response.',
  },
  {
    id: 'step-score',
    title: 'Step 3: Score and Submit',
    detail: 'Finally, score the 3 dimensions and submit to continue.',
  },
];

const DIMENSIONS: DimensionConfig[] = [
  {
    key: 'emotionalAuthenticityScore',
    label: 'Emotional Authenticity',
    short: 'Emotional Authenticity',
    levels: {
      1: {
        title: 'Soap Opera / Fake',
        detail: 'Sounds like soap-opera acting: fake, overly polished, or overly composed.',
      },
      2: {
        title: 'In Between',
        detail: 'In the middle: not clearly fake, not strongly real.',
      },
      3: {
        title: 'Visceral/Real',
        detail: 'Feels human and real, clearly not soap-opera acting.',
      },
    },
  },
  {
    key: 'groundingScore',
    label: 'Grounding',
    short: 'Grounding',
    levels: {
      1: {
        title: 'Scripted',
        detail:
          "Ignores the clinician's last sentence/question and gives a generic line (e.g., asked about allergies, replies 'We need a miracle').",
      },
      2: {
        title: 'Relevant',
        detail: "Responds to the topic but stays broad, without directly using the clinician's exact wording.",
      },
      3: {
        title: 'Connected',
        detail:
          "Directly references the clinician's latest sentence and builds logically (e.g., doctor mentions surgery risk, parent asks about that risk).",
      },
    },
  },
  {
    key: 'topicConsistencyScore',
    label: 'Topic Consistency',
    short: 'Topic Consistency',
    levels: {
      1: {
        title: 'Scattered',
        detail: "Jumps across unrelated topics without linkage (e.g., 'God' to money to second opinion).",
      },
      2: {
        title: 'Dual Focus',
        detail: 'Mentions two concerns and connects them adequately.',
      },
      3: {
        title: 'Focused',
        detail: "Stays on one main worry effectively (e.g., 'Will he wake up?').",
      },
    },
  },
];

const SCORE_HEADERS: Record<ScoreValue, string> = {
  1: '1 Poor / Fail',
  2: '2 Acceptable',
  3: '3 Excellent',
};

const getEmptyScores = (): Record<ScoreKey, ScoreValue | null> => ({
  emotionalAuthenticityScore: null,
  groundingScore: null,
  topicConsistencyScore: null,
});

const getEmptyDimensionNotes = (): DimensionNotes => ({
  emotionalAuthenticity: '',
  grounding: '',
  topicConsistency: '',
});

const serializeDimensionNotes = (dimensionNotes: DimensionNotes): string => {
  const sections = [
    { label: 'Emotional Authenticity', value: dimensionNotes.emotionalAuthenticity.trim() },
    { label: 'Grounding', value: dimensionNotes.grounding.trim() },
    { label: 'Topic Consistency', value: dimensionNotes.topicConsistency.trim() },
  ];

  return sections
    .filter((section) => section.value.length > 0)
    .map((section) => `${section.label} Note:\n${section.value}`)
    .join('\n\n');
};

const parseDimensionNotes = (raw: string): DimensionNotes => {
  const notes = getEmptyDimensionNotes();
  const text = raw.trim();
  if (!text || text.toLowerCase() === 'no comment') return notes;

  const lines = text.split('\n');
  let current: keyof DimensionNotes | null = null;
  const buffer: Record<keyof DimensionNotes, string[]> = {
    emotionalAuthenticity: [],
    grounding: [],
    topicConsistency: [],
  };

  const setCurrentFromHeader = (line: string): keyof DimensionNotes | null => {
    const normalized = line.toLowerCase();
    if (normalized.startsWith('emotional authenticity note:')) return 'emotionalAuthenticity';
    if (normalized.startsWith('grounding note:')) return 'grounding';
    if (normalized.startsWith('topic consistency note:')) return 'topicConsistency';
    return null;
  };

  for (const line of lines) {
    const header = setCurrentFromHeader(line.trim());
    if (header) {
      current = header;
      continue;
    }
    if (current) {
      buffer[current].push(line);
    }
  }

  notes.emotionalAuthenticity = buffer.emotionalAuthenticity.join('\n').trim();
  notes.grounding = buffer.grounding.join('\n').trim();
  notes.topicConsistency = buffer.topicConsistency.join('\n').trim();

  if (!notes.emotionalAuthenticity && !notes.grounding && !notes.topicConsistency) {
    notes.emotionalAuthenticity = text;
  }

  return notes;
};

const CalibrationPage: React.FC = () => {
  const [currentItem, setCurrentItem] = useState<CalibrationItemDto | null>(null);
  const [progress, setProgress] = useState<CalibrationProgress | null>(null);
  const [items, setItems] = useState<CalibrationListItem[]>([]);
  const [scores, setScores] = useState<Record<ScoreKey, ScoreValue | null>>(getEmptyScores());
  const [dimensionNotes, setDimensionNotes] = useState<DimensionNotes>(getEmptyDimensionNotes());
  const [isLoadingItem, setIsLoadingItem] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [showFullContext, setShowFullContext] = useState(false);
  const [showRubricMatrix, setShowRubricMatrix] = useState(false);
  const [showStepGuide, setShowStepGuide] = useState(true);
  const [activeStep, setActiveStep] = useState<StepId>('step-rubric');
  const [isViewingRated, setIsViewingRated] = useState(false);

  const loadItems = useCallback(async () => {
    try {
      const res = await apiService.getCalibrationItems();
      setItems(res.items || []);
    } catch (err) {
      console.error('Failed to load calibration turns list', err);
    }
  }, []);

  const loadProgress = useCallback(async () => {
    try {
      const p = await apiService.getMyCalibrationProgress();
      setProgress(p);
    } catch (err) {
      console.error('Failed to load calibration progress', err);
    }
  }, []);

  const loadNextItem = useCallback(async () => {
    setIsLoadingItem(true);
    setMessage(null);
    try {
      const res = await apiService.getNextCalibrationItem();
      if (res.done || !res.item) {
        setDone(true);
        setCurrentItem(null);
      } else {
        setCurrentItem(res.item);
        setDone(false);
        setScores(getEmptyScores());
        setDimensionNotes(getEmptyDimensionNotes());
        setIsViewingRated(false);
      }
      setShowFullContext(false);
      setActiveStep('step-rubric');
    } catch (err) {
      console.error('Failed to load next calibration turn', err);
      setMessage('Failed to load the next calibration turn. Please try again.');
    } finally {
      setIsLoadingItem(false);
    }
  }, []);

  useEffect(() => {
    loadProgress();
    loadItems();
    loadNextItem();
  }, [loadProgress, loadNextItem, loadItems]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);

        if (visible.length > 0) {
          const id = visible[0].target.id as StepId;
          setActiveStep(id);
        }
      },
      {
        root: null,
        threshold: [0.2, 0.45, 0.7],
        rootMargin: '-140px 0px -38% 0px',
      },
    );

    STEP_CONFIG.forEach((step) => {
      const element = document.getElementById(step.id);
      if (element) observer.observe(element);
    });

    return () => observer.disconnect();
  }, [done, currentItem?.itemId]);

  const handleSelectItem = async (itemId: string) => {
    setIsLoadingItem(true);
    setMessage(null);
    try {
      const res = await apiService.getCalibrationItem(itemId);
      setCurrentItem(res.item);
      setDone(false);
      setShowFullContext(false);
      setActiveStep('step-rubric');
      if (res.item.existingRating) {
        const r = res.item.existingRating;
        setScores({
          emotionalAuthenticityScore: r.emotionalAuthenticityScore as ScoreValue,
          groundingScore: r.groundingScore as ScoreValue,
          topicConsistencyScore: r.topicConsistencyScore as ScoreValue,
        });
        setDimensionNotes(parseDimensionNotes(r.notes ?? ''));
        setIsViewingRated(true);
      } else {
        setScores(getEmptyScores());
        setDimensionNotes(getEmptyDimensionNotes());
        setIsViewingRated(false);
      }
    } catch (err) {
      console.error('Failed to load calibration turn', err);
      setMessage('Failed to load that turn. Please try again.');
    } finally {
      setIsLoadingItem(false);
    }
  };

  const handleScoreChange = (key: ScoreKey, value: ScoreValue) => {
    setScores((prev) => ({ ...prev, [key]: value }));
  };

  const handleDimensionNoteChange = (key: keyof DimensionNotes, value: string) => {
    setDimensionNotes((prev) => ({ ...prev, [key]: value }));
  };

  const allScoresFilled = DIMENSIONS.every((d) => scores[d.key] != null);

  const handleSubmit = async () => {
    if (!currentItem || !allScoresFilled || isSubmitting) return;

    setIsSubmitting(true);
    setMessage(null);
    try {
      const serializedNotes = serializeDimensionNotes(dimensionNotes).trim();
      await apiService.submitCalibrationRating(currentItem.itemId, {
        emotionalAuthenticityScore: scores.emotionalAuthenticityScore as ScoreValue,
        groundingScore: scores.groundingScore as ScoreValue,
        topicConsistencyScore: scores.topicConsistencyScore as ScoreValue,
        // Backward compatibility: older backend may still require these fields.
        informationFogScore: 2,
        relationshipTextureScore: 2,
        // Backward compatibility: older backend may still require non-empty notes.
        notes: serializedNotes || 'No comment',
      });

      setScores(getEmptyScores());
      setDimensionNotes(getEmptyDimensionNotes());
      await loadProgress();
      await loadItems();
      await loadNextItem();
      setMessage('Rating saved. Loading the next turn...');
    } catch (err) {
      console.error('Failed to submit calibration rating', err);
      const errorMessage =
        (err as any)?.response?.data?.error || 'Failed to submit rating. Please try again.';
      setMessage(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const scrollToStep = (stepId: StepId) => {
    setActiveStep(stepId);
    if (stepId === 'step-rubric') {
      setShowRubricMatrix(true);
    }
    const target = document.getElementById(stepId);
    if (target) {
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const totalItems = progress?.totalItems ?? 0;
  const ratedCount = progress?.ratedCount ?? 0;
  const remaining = progress?.remaining ?? Math.max(totalItems - ratedCount, 0);
  const percent = totalItems > 0 ? Math.round((ratedCount / totalItems) * 100) : 0;

  const contextToShow: CalibrationContextTurn[] = useMemo(() => {
    if (!currentItem) return [];
    const all = currentItem.contextFull || [];
    let list = showFullContext || all.length <= 5 ? [...all] : all.slice(-5);
    // Don't show the last clinician line in Previous Context — it's already in "Grounding anchor: clinician last line"
    if (list.length > 0 && list[list.length - 1].role === 'clinician') {
      list = list.slice(0, -1);
    }
    return list;
  }, [currentItem, showFullContext]);

  const queueByCase: QueueCaseGroup[] = useMemo(() => {
    const groups = new Map<number, QueueCaseGroup>();

    items.forEach((item) => {
      const caseNumber = item.caseNumber;
      if (!groups.has(caseNumber)) {
        groups.set(caseNumber, {
          caseNumber,
          caseLabel: item.caseLabel || `Case ${caseNumber}`,
          turns: [],
        });
      }
      groups.get(caseNumber)?.turns.push(item);
    });

    return Array.from(groups.values()).sort((a, b) => a.caseNumber - b.caseNumber);
  }, [items]);

  const currentItemListMeta = useMemo(
    () => items.find((i) => i.itemId === currentItem?.itemId),
    [items, currentItem?.itemId],
  );

  const currentTurnLabel = currentItem
    ? `${currentItem.caseLabel || `Case ${currentItem.caseNumber}`} - Turn ${currentItem.turnInCase}/${currentItem.turnsInCase}`
    : 'No turn selected';

  const renderContextTurn = (turn: CalibrationContextTurn) => {
    const isClinician = turn.role === 'clinician';
    return (
      <article
        key={`${turn.turn_index}-${turn.role}-${turn.speaker}`}
        className={`rounded-xl border px-4 py-3.5 ${
          isClinician ? 'border-sky-200 bg-sky-50/80' : 'border-rose-200 bg-rose-50/80'
        }`}
      >
        <header className="mb-2 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <span
              className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide ${
                isClinician
                  ? 'border-sky-300 bg-white text-sky-700'
                  : 'border-rose-300 bg-white text-rose-700'
              }`}
            >
              {isClinician ? 'Clinician' : 'Parent'}
            </span>
            <span className="text-xs text-slate-500">{turn.speaker}</span>
          </div>
          <span className="text-xs text-slate-400">Turn {turn.turn_index}</span>
        </header>
        <p className="whitespace-pre-wrap text-sm leading-relaxed text-slate-800">{turn.text}</p>
      </article>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-sky-50 to-indigo-50">
      <div className="border-b border-indigo-100 bg-white/95">
        <div className="mx-auto max-w-7xl px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <h1 className="text-xl font-bold text-slate-900 sm:text-2xl">
                Calibration: Parent Response Rubric
              </h1>
              <p className="mt-1 max-w-3xl text-xs text-slate-600 sm:text-sm">
                Review context, score the highlighted parent response, and submit one turn at a time.
              </p>
            </div>
            <div className="rounded-xl border border-slate-200 bg-white px-3 py-2">
              <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-600">
                Progress
              </div>
              <div className="mt-1 h-2 w-52 overflow-hidden rounded-full bg-slate-100">
                <div
                  className="h-full bg-gradient-to-r from-indigo-500 to-sky-500"
                  style={{ width: `${percent}%` }}
                />
              </div>
              <div className="mt-1 flex justify-between text-[11px] text-slate-500">
                <span>
                  {ratedCount} / {totalItems || '--'} turns
                </span>
                <span>{percent}%</span>
              </div>
            </div>
          </div>

          <div className="mt-3">
            <button
              type="button"
              onClick={() => setShowStepGuide((prev) => !prev)}
              aria-expanded={showStepGuide}
              className="inline-flex items-center rounded-lg border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-semibold text-slate-700 transition-colors hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
            >
              {showStepGuide ? 'Hide 3-step guide' : 'Show 3-step guide'}
            </button>
          </div>

          {showStepGuide && (
            <>
              <nav
                aria-label="Calibration workflow"
                className="mt-3 overflow-x-auto rounded-xl border border-slate-200 bg-white px-2 py-2"
              >
                <ol className="flex min-w-max items-stretch gap-2">
                  {STEP_CONFIG.map((step) => {
                    const isActive = activeStep === step.id;
                    return (
                      <li key={step.id}>
                        <button
                          type="button"
                          onClick={() => scrollToStep(step.id)}
                          className={`w-64 rounded-lg border px-3 py-2 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 ${
                            isActive
                              ? 'border-indigo-600 bg-indigo-600 text-white'
                              : 'border-slate-200 bg-slate-50 text-slate-700 hover:bg-slate-100'
                          }`}
                        >
                          <div className="text-[12px] font-semibold">{step.title}</div>
                          <div className="mt-0.5 text-[11px] leading-snug opacity-90">{step.detail}</div>
                        </button>
                      </li>
                    );
                  })}
                </ol>
              </nav>

              <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 px-3 py-3">
                <div className="text-xs font-semibold uppercase tracking-wide text-amber-800">
                  First-time rater quick start
                </div>
                <ol className="mt-1 list-decimal space-y-1 pl-4 text-xs text-amber-900">
                  <li>Read the rubric matrix first so 1/2/3 means the same thing each time.</li>
                  <li>Read the context and the clinician last line before judging the target response.</li>
                  <li>Score all three dimensions, add comments when useful, then submit and move to next turn.</li>
                </ol>
              </div>
            </>
          )}

          <section
            id="step-rubric"
            className={`mt-3 rounded-2xl bg-white p-3 shadow-sm transition-all ${
              showStepGuide && activeStep === 'step-rubric'
                ? 'border-2 border-dashed border-rose-400 ring-2 ring-rose-100'
                : 'border border-slate-200'
            }`}
          >
            {showStepGuide && activeStep === 'step-rubric' && (
              <div className="mb-3 inline-flex items-center rounded-full border border-rose-300 bg-rose-50 px-2.5 py-1 text-[11px] font-semibold text-rose-700">
                Focus here for Step 1
              </div>
            )}

            <button
              type="button"
              onClick={() => setShowRubricMatrix((prev) => !prev)}
              aria-expanded={showRubricMatrix}
              className="inline-flex items-center rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-1.5 text-xs font-semibold text-indigo-700 transition-colors hover:bg-indigo-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
            >
              {showRubricMatrix ? 'Hide rubric matrix' : 'Show rubric matrix'}
            </button>

            {showRubricMatrix ? (
              <div className="mt-3 overflow-x-auto rounded-xl border border-slate-200 bg-white">
                <table className="min-w-[920px] w-full border-collapse text-xs">
                  <thead className="bg-slate-50 text-slate-700">
                    <tr>
                      <th className="border-b border-slate-200 px-3 py-2 text-left font-semibold">
                        Dimension
                      </th>
                      <th className="border-b border-slate-200 px-3 py-2 text-left font-semibold text-red-700">
                        1 (Poor / Fail)
                      </th>
                      <th className="border-b border-slate-200 px-3 py-2 text-left font-semibold text-amber-700">
                        2 (Acceptable)
                      </th>
                      <th className="border-b border-slate-200 px-3 py-2 text-left font-semibold text-emerald-700">
                        3 (Excellent)
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {DIMENSIONS.map((dim) => (
                      <tr key={dim.key} className="align-top">
                        <td className="border-b border-slate-100 px-3 py-2.5 font-semibold text-slate-800">
                          {dim.short}
                        </td>
                      {[1, 2, 3].map((score) => {
                        const level = dim.levels[score as ScoreValue];
                        return (
                          <td
                            key={`${dim.key}-${score}`}
                            className="border-b border-slate-100 px-3 py-2.5 text-slate-700"
                          >
                              <div className="text-[11px] leading-relaxed text-slate-600">{level.detail}</div>
                          </td>
                        );
                      })}
                    </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="mt-3 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
                Rubric matrix is hidden.
              </div>
            )}
          </section>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
        <div className="sr-only" aria-live="polite">
          {message || ''}
        </div>

        {!isLoadingItem && done && (
          <section className="rounded-2xl border border-emerald-200 bg-white p-8 text-center shadow-sm">
            <h2 className="text-xl font-semibold text-slate-900">You completed all calibration turns</h2>
            <p className="mx-auto mt-2 max-w-2xl text-sm text-slate-600">
              Thank you for contributing ratings. If new calibration turns are imported, they will
              appear automatically in your queue.
            </p>
            <div className="mt-4 inline-flex items-center rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">
              Completed {ratedCount} turn{ratedCount === 1 ? '' : 's'}
            </div>
          </section>
        )}

        {!done && (
          <div className="grid grid-cols-1 gap-6 xl:grid-cols-[1.12fr_1fr]">
            <div className="space-y-4">
              <section
                id="step-context"
                className={`rounded-2xl bg-white p-4 shadow-sm transition-all sm:p-5 ${
                  showStepGuide && activeStep === 'step-context'
                    ? 'border-2 border-dashed border-rose-400 ring-2 ring-rose-100'
                    : 'border border-slate-200'
                }`}
              >
                {showStepGuide && activeStep === 'step-context' && (
                  <div className="mb-3 inline-flex items-center rounded-full border border-rose-300 bg-rose-50 px-2.5 py-1 text-[11px] font-semibold text-rose-700">
                    Focus here for Step 2
                  </div>
                )}
                <div className="mb-3 flex items-start justify-between gap-3">
                  <div>
                    <h2 className="text-sm font-semibold text-slate-900">Previous Context</h2>
                    <p className="mt-0.5 text-[11px] text-slate-500">
                      Default: last 5 utterances before the target parent response.
                    </p>
                  </div>
                  {currentItem && currentItem.contextFull.length > 5 && (
                    <button
                      type="button"
                      onClick={() => setShowFullContext((prev) => !prev)}
                      className="rounded-lg border border-indigo-200 bg-indigo-50 px-2.5 py-1 text-[11px] font-semibold text-indigo-700 transition-colors hover:bg-indigo-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
                    >
                      {showFullContext ? 'Show last 5 turns' : 'Show full context'}
                    </button>
                  )}
                </div>

                {isLoadingItem ? (
                  <div className="space-y-3">
                    {[0, 1, 2].map((n) => (
                      <div
                        key={n}
                        className="animate-pulse rounded-xl border border-slate-200 bg-slate-50 p-4"
                      >
                        <div className="mb-2 h-3 w-24 rounded bg-slate-200" />
                        <div className="h-3 w-full rounded bg-slate-200" />
                        <div className="mt-2 h-3 w-10/12 rounded bg-slate-200" />
                      </div>
                    ))}
                  </div>
                ) : currentItem ? (
                  <div className="max-h-[620px] space-y-3 overflow-y-auto pr-1">{contextToShow.map(renderContextTurn)}</div>
                ) : (
                  <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-4 text-sm text-slate-500">
                    No calibration turn selected.
                  </div>
                )}
              </section>

              <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
                <div className="mb-3 flex items-center justify-between gap-3">
                  <div>
                    <h2 className="text-sm font-semibold text-slate-900">Turn Queue by Case</h2>
                    <p className="text-[11px] text-slate-500">
                      Use this to jump across turns after you finish your current review.
                    </p>
                  </div>
                  <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-600">
                    Remaining: {remaining}
                  </span>
                </div>

                {items.length === 0 ? (
                  <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 px-3 py-4 text-xs text-slate-600">
                    No calibration turns are loaded yet. Import the calibration set and refresh this page.
                  </div>
                ) : (
                  <div className="max-h-72 space-y-3 overflow-y-auto pr-1">
                    {queueByCase.map((group) => {
                      const ratedTurns = group.turns.filter((turn) => turn.hasRating).length;
                      return (
                        <section key={group.caseNumber} className="rounded-xl border border-slate-200 bg-slate-50 p-2.5">
                          <div className="mb-2 flex items-center justify-between px-1">
                            <h3 className="text-xs font-semibold text-slate-800">{group.caseLabel}</h3>
                            <span className="text-[11px] text-slate-500">
                              {ratedTurns}/{group.turns.length} rated
                            </span>
                          </div>
                          <div className="space-y-1.5">
                            {group.turns.map((turn) => {
                              const isCurrent = currentItem?.itemId === turn.itemId;
                              const classes = isCurrent
                                ? 'border-indigo-600 bg-indigo-600 text-white'
                                : turn.hasRating
                                ? 'border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
                                : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-100';

                              return (
                                <button
                                  key={turn.itemId}
                                  type="button"
                                  onClick={() => handleSelectItem(turn.itemId)}
                                  className={`flex w-full items-center justify-between rounded-lg border px-3 py-2 text-left text-xs transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 ${classes}`}
                                >
                                  <span className="font-medium">
                                    Turn {turn.turnInCase}/{turn.turnsInCase}
                                  </span>
                                  <span className="text-[10px] uppercase tracking-wide">
                                    {turn.hasRating ? 'Rated' : 'Unrated'}
                                  </span>
                                </button>
                              );
                            })}
                          </div>
                        </section>
                      );
                    })}
                  </div>
                )}
              </section>
            </div>

            <div className="space-y-4">
              <section
                className={`rounded-2xl bg-white p-4 shadow-sm transition-all sm:p-5 ${
                  showStepGuide && activeStep === 'step-context'
                    ? 'border-2 border-dashed border-rose-400 ring-2 ring-rose-100'
                    : 'border border-slate-200'
                }`}
              >
                {showStepGuide && activeStep === 'step-context' && (
                  <div className="mb-3 inline-flex items-center rounded-full border border-rose-300 bg-rose-50 px-2.5 py-1 text-[11px] font-semibold text-rose-700">
                    Focus here for Step 2
                  </div>
                )}
                <div className="mb-3 rounded-xl border border-indigo-200 bg-indigo-50 px-4 py-3">
                  <div className="text-xs font-semibold uppercase tracking-wide text-indigo-700">
                    Current selection
                  </div>
                  <div className="mt-1 text-sm font-semibold text-slate-900">{currentTurnLabel}</div>
                </div>

                <div className="mb-3 rounded-xl border border-sky-200 bg-sky-50/80 px-4 py-3">
                  <div className="text-xs font-semibold uppercase tracking-wide text-sky-700">
                    Grounding anchor: clinician last line
                  </div>
                  {currentItem ? (
                    <p className="mt-1 whitespace-pre-wrap text-sm leading-relaxed text-slate-800">
                      {currentItem.clinicianLastTurn}
                    </p>
                  ) : (
                    <div className="mt-2 h-4 w-4/5 animate-pulse rounded bg-sky-100" />
                  )}
                </div>

                <div className="rounded-xl border-2 border-indigo-300 bg-indigo-50/70 px-4 py-3.5">
                  <h2 className="text-sm font-semibold text-indigo-900">Target Parent Response</h2>
                  <p className="text-[11px] font-medium text-indigo-700">Read this line before scoring.</p>
                  {isLoadingItem ? (
                    <div className="mt-2 space-y-2 animate-pulse">
                      <div className="h-4 w-full rounded bg-indigo-100" />
                      <div className="h-4 w-5/6 rounded bg-indigo-100" />
                    </div>
                  ) : currentItem ? (
                    <p className="mt-2 whitespace-pre-wrap text-base leading-relaxed text-slate-900">
                      {currentItem.targetParentTurnText}
                    </p>
                  ) : (
                    <p className="mt-2 text-sm text-slate-500">No turn available.</p>
                  )}
                </div>
              </section>

              <section
                id="step-score"
                className={`rounded-2xl bg-white p-4 shadow-sm transition-all sm:p-5 ${
                  showStepGuide && activeStep === 'step-score'
                    ? 'border-2 border-dashed border-rose-400 ring-2 ring-rose-100'
                    : 'border border-slate-200'
                }`}
              >
                {showStepGuide && activeStep === 'step-score' && (
                  <div className="mb-3 inline-flex items-center rounded-full border border-rose-300 bg-rose-50 px-2.5 py-1 text-[11px] font-semibold text-rose-700">
                    Focus here for Step 3
                  </div>
                )}

                {isViewingRated ? (
                  <>
                    <div className="mb-3 rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-2 text-xs text-indigo-800">
                      Your previous rating (read-only). Click Resubmit below to change it.
                    </div>
                    <div className="mt-4 space-y-3">
                      {DIMENSIONS.map((dim) => {
                        const value = scores[dim.key] as ScoreValue | null;
                        return (
                          <div key={dim.key} className="rounded-xl border border-slate-200 bg-slate-50/50 p-3">
                            <div className="text-xs font-semibold text-slate-900">{dim.label}</div>
                            <div className="mt-1.5 text-sm text-slate-700">
                              {value != null ? (
                                <span className="font-medium">{SCORE_HEADERS[value]}</span>
                              ) : (
                                '—'
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    <div className="mt-4 space-y-2">
                      <div className="text-xs font-semibold text-slate-800">Your notes (stored as one text field)</div>
                      <div className="rounded-lg border border-slate-200 bg-slate-50/50 px-3 py-2 text-sm text-slate-800">
                        <div className="text-[11px] font-semibold text-slate-700">Emotional Authenticity</div>
                        <div className="mt-1 whitespace-pre-wrap">{dimensionNotes.emotionalAuthenticity || '—'}</div>
                      </div>
                      <div className="rounded-lg border border-slate-200 bg-slate-50/50 px-3 py-2 text-sm text-slate-800">
                        <div className="text-[11px] font-semibold text-slate-700">Grounding</div>
                        <div className="mt-1 whitespace-pre-wrap">{dimensionNotes.grounding || '—'}</div>
                      </div>
                      <div className="rounded-lg border border-slate-200 bg-slate-50/50 px-3 py-2 text-sm text-slate-800">
                        <div className="text-[11px] font-semibold text-slate-700">Topic Consistency</div>
                        <div className="mt-1 whitespace-pre-wrap">{dimensionNotes.topicConsistency || '—'}</div>
                      </div>
                    </div>
                    <div className="mt-4 flex justify-end">
                      <button
                        type="button"
                        onClick={() => setIsViewingRated(false)}
                        className="inline-flex items-center rounded-lg border border-indigo-600 bg-white px-4 py-2 text-sm font-semibold text-indigo-600 shadow-sm hover:bg-indigo-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
                      >
                        Resubmit (edit rating)
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    {currentItemListMeta?.hasRating && (
                      <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                        You are revisiting a rated turn. Submitting again will overwrite your previous rating.
                      </div>
                    )}

                    <div className="mt-4 space-y-3">
                      {DIMENSIONS.map((dim) => (
                        <div key={dim.key} className="rounded-xl border border-slate-200 p-3">
                          <div className="text-xs font-semibold text-slate-900">{dim.label}</div>
                          <div className="mt-2 grid grid-cols-3 gap-1.5">
                            {[1, 2, 3].map((score) => {
                              const selected = scores[dim.key] === score;
                              return (
                                <button
                                  key={`${dim.key}-${score}`}
                                  type="button"
                                  aria-pressed={selected}
                                  aria-label={`${dim.label} score ${score}: ${SCORE_HEADERS[score as ScoreValue]}`}
                                  onClick={() => handleScoreChange(dim.key, score as ScoreValue)}
                                  className={`rounded-lg border px-2 py-2 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 ${
                                    selected
                                      ? 'border-indigo-600 bg-indigo-600 text-white'
                                      : 'border-slate-200 bg-slate-50 text-slate-700 hover:bg-slate-100'
                                  }`}
                                >
                                  <div className="text-[11px] font-semibold">{SCORE_HEADERS[score as ScoreValue]}</div>
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="mt-4 space-y-3">
                      <div>
                        <label className="mb-1 block text-xs font-semibold text-slate-800">
                          Emotional Authenticity note
                        </label>
                        <textarea
                          className="min-h-[62px] w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          placeholder="If score is 1 or 3, explain why. For 2, comment is optional."
                          value={dimensionNotes.emotionalAuthenticity}
                          onChange={(e) =>
                            handleDimensionNoteChange('emotionalAuthenticity', e.target.value)
                          }
                        />
                      </div>
                      <div>
                        <label className="mb-1 block text-xs font-semibold text-slate-800">
                          Grounding note
                        </label>
                        <textarea
                          className="min-h-[62px] w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          placeholder="If score is 1 or 3, explain why. For 2, comment is optional."
                          value={dimensionNotes.grounding}
                          onChange={(e) => handleDimensionNoteChange('grounding', e.target.value)}
                        />
                      </div>
                      <div>
                        <label className="mb-1 block text-xs font-semibold text-slate-800">
                          Topic Consistency note
                        </label>
                        <textarea
                          className="min-h-[62px] w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          placeholder="If score is 1 or 3, explain why. For 2, comment is optional."
                          value={dimensionNotes.topicConsistency}
                          onChange={(e) => handleDimensionNoteChange('topicConsistency', e.target.value)}
                        />
                      </div>
                      <div className="mt-2 grid gap-2 sm:grid-cols-2">
                        <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-[11px] text-emerald-900">
                          <div className="font-semibold">Good note example</div>
                          <p className="mt-1">
                            "Emotional Authenticity note: sounds theatrical and overly polished, which feels
                            inauthentic for this moment.
                            Grounding note: directly answers the clinician's allergy question and clearly
                            connects to the last clinician sentence.
                            Topic Consistency note (optional for 2): mostly stays on one concern, but has
                            a slight topic shift near the end."
                          </p>
                        </div>
                        <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-[11px] text-rose-900">
                          <div className="font-semibold">Weak note example</div>
                          <p className="mt-1">"Seems okay."</p>
                        </div>
                      </div>
                    </div>

                    {message && (
                      <div
                        className="mt-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700"
                        role="status"
                        aria-live="polite"
                      >
                        {message}
                      </div>
                    )}

                    <div className="mt-4 flex items-center justify-between gap-3">
                      <div className="text-[11px] text-slate-500">
                        Comments are optional. Use notes mainly to explain clear strengths (3) or failures (1).
                      </div>
                      <button
                        type="button"
                        onClick={handleSubmit}
                        disabled={!allScoresFilled || !currentItem || isSubmitting}
                        className={`inline-flex items-center rounded-lg px-4 py-2 text-sm font-semibold shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 ${
                          allScoresFilled && currentItem && !isSubmitting
                            ? 'bg-indigo-600 text-white hover:bg-indigo-700'
                            : 'cursor-not-allowed bg-slate-200 text-slate-500'
                        }`}
                      >
                        {isSubmitting ? 'Saving...' : 'Submit and Next'}
                      </button>
                    </div>
                  </>
                )}
              </section>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CalibrationPage;
